import { TASK_SOURCE_LABELS, type TaskUpstream } from '@shared/types/task-sources';
import type { TaskEvent } from '@shared/types/tasks';

/**
 * T-15: a message also posted on the task's source says so (or that it couldn't be). Messages synced
 * in from upstream already say where they're from in their name ("Deon · Asana").
 */
export function upstreamNote(event: Pick<TaskEvent, 'payload'>, upstream: TaskUpstream | null | undefined): string {
  const info = event.payload?.upstream as { posted?: boolean } | undefined;
  if (!info || info.posted === undefined) return '';
  const where = upstream ? `to ${TASK_SOURCE_LABELS[upstream.kind]}` : 'upstream';
  return info.posted ? ` · posted ${where}` : ` · couldn’t post ${where}`;
}

/** Written upstream and synced in (T-15): a comment, or the line saying the source created the task. */
export function isSyncedIn(event: Pick<TaskEvent, 'payload' | 'actor_label'>): boolean {
  const info = event.payload?.upstream as { posted?: boolean } | undefined;
  return Boolean(event.actor_label && info && info.posted === undefined);
}

/**
 * T-99: on a line linking to upstream files too big to copy in, where each thumbnail's file opens
 * upstream (by attachment id), web addresses only. Empty for every other message.
 */
export function upstreamOpens(event: Pick<TaskEvent, 'payload'>): Record<string, string> {
  const opens = (event.payload?.upstream as { opens?: unknown } | undefined)?.opens;
  if (!opens || typeof opens !== 'object') return {};
  return Object.fromEntries(Object.entries(opens).filter((e): e is [string, string] => typeof e[1] === 'string' && /^https?:\/\//i.test(e[1])));
}

/** Every thumbnail on the task whose file is upstream (T-99), by attachment id, with where it opens. */
export function taskUpstreamOpens(events: Array<Pick<TaskEvent, 'payload'>>): Record<string, string> {
  return Object.assign({}, ...events.map(upstreamOpens)) as Record<string, string>;
}

/**
 * SF-T-201: a comment that "Send to <source>" can take: one made here, not up there yet. Not one synced in, not
 * one already posted, and not a send of the AI builder's questions or suggestions (T-58), which go from their
 * card. One that couldn't post can go again. The worker checks the same, and that it's more than blocks.
 */
export function canSendComment(event: Pick<TaskEvent, 'kind' | 'payload'>): boolean {
  if (event.kind !== 'chat') return false;
  const payload = event.payload ?? {};
  if ('deferred' in payload || 'deferred_failed' in payload) return false;
  const info = payload.upstream as { posted?: boolean } | undefined;
  return !info || info.posted === false;
}
